import { useEffect, useMemo, useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight, Plus, Trash2, Gavel } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { listHearings, createHearing, deleteHearing, listCases } from "@/lib/api";

const MONTHS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const DAYS_SHORT = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];

function monthKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function dayKey(d) { return d.toISOString().slice(0, 10); }

function buildCalendar(year, month) {
  const first = new Date(year, month, 1);
  const startDow = (first.getDay() + 6) % 7; // Mon=0
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export default function HearingsPage() {
  const now = new Date();
  const [cursor, setCursor] = useState(new Date(now.getFullYear(), now.getMonth(), 1));
  const [hearings, setHearings] = useState([]);
  const [cases, setCases] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ case_id: "", hearing_date: "", hearing_time: "", note: "", status: "planlanmis" });

  const load = async () => {
    try {
      const [hs, cs] = await Promise.all([listHearings(monthKey(cursor)), listCases()]);
      setHearings(hs); setCases(cs);
    } catch { toast.error("Veriler yüklenemedi"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cursor]);

  const byDay = useMemo(() => {
    const map = {};
    for (const h of hearings) (map[h.hearing_date] ||= []).push(h);
    return map;
  }, [hearings]);

  const cells = useMemo(() => buildCalendar(cursor.getFullYear(), cursor.getMonth()), [cursor]);
  const today = dayKey(new Date());

  const prev = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  const next = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
  const todayBtn = () => setCursor(new Date(now.getFullYear(), now.getMonth(), 1));

  const submit = async () => {
    if (!form.case_id || !form.hearing_date) {
      toast.error("Dava ve tarih zorunludur"); return;
    }
    try {
      await createHearing(form.case_id, {
        hearing_date: form.hearing_date,
        hearing_time: form.hearing_time,
        note: form.note,
        status: form.status,
      });
      toast.success("Duruşma eklendi");
      setForm({ case_id: "", hearing_date: "", hearing_time: "", note: "", status: "planlanmis" });
      setNewOpen(false);
      load();
    } catch { toast.error("Duruşma eklenemedi"); }
  };

  const removeHearing = async (id) => {
    try { await deleteHearing(id); toast.success("Duruşma silindi"); load(); }
    catch { toast.error("Silinemedi"); }
  };

  const statusBadge = (s) => {
    if (s === "yapildi") return <Badge className="bg-emerald-700 text-white">Yapıldı</Badge>;
    if (s === "ertelendi") return <Badge className="bg-amber-700 text-white">Ertelendi</Badge>;
    return <Badge className="bg-slate-700 text-slate-100">Planlanmış</Badge>;
  };

  return (
    <div className="p-6 space-y-5" data-testid="hearings-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <CalendarDays className="w-7 h-7 text-amber-500" />
            <h1 className="font-serif text-3xl font-bold text-slate-100">Duruşma Takvimi</h1>
          </div>
          <p className="text-slate-400 mt-1 text-sm">Tüm duruşmalarınızı aylık görünümde takip edin</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={prev} className="border-slate-700 text-slate-200" data-testid="cal-prev-btn"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" onClick={todayBtn} className="text-amber-500" data-testid="cal-today-btn">Bugün</Button>
          <div className="font-serif text-xl text-slate-100 min-w-[180px] text-center" data-testid="cal-month-label">
            {MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
          </div>
          <Button variant="outline" size="sm" onClick={next} className="border-slate-700 text-slate-200" data-testid="cal-next-btn"><ChevronRight className="w-4 h-4" /></Button>
          <Button size="sm" onClick={() => setNewOpen(true)} className="bg-amber-600 hover:bg-amber-700 text-slate-950" data-testid="new-hearing-btn">
            <Plus className="w-4 h-4 mr-1" /> Duruşma Ekle
          </Button>
        </div>
      </div>

      <Card className="bg-slate-900/60 border-slate-800">
        <CardContent className="p-4">
          <div className="grid grid-cols-7 gap-1 mb-1">
            {DAYS_SHORT.map((d) => (
              <div key={d} className="text-[10px] font-mono uppercase tracking-widest text-slate-500 text-center py-2">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((d, i) => {
              if (!d) return <div key={i} className="h-28 rounded bg-slate-950/40" />;
              const k = dayKey(d);
              const items = byDay[k] || [];
              const isToday = k === today;
              return (
                <div key={i}
                  className={`h-28 rounded border p-1.5 flex flex-col gap-1 overflow-hidden transition
                    ${isToday ? "border-amber-500 bg-amber-500/5" : "border-slate-800 bg-slate-900/40 hover:border-slate-700"}`}
                  data-testid={`cal-day-${k}`}
                >
                  <div className={`text-xs font-mono ${isToday ? "text-amber-500 font-bold" : "text-slate-400"}`}>{d.getDate()}</div>
                  <div className="flex flex-col gap-0.5 overflow-y-auto">
                    {items.map((h) => (
                      <div key={h.id} className="text-[10px] bg-slate-800/80 border-l-2 border-amber-500 pl-1 pr-1 py-0.5 rounded truncate flex items-center gap-1"
                        title={`${h.dava_no} — ${h.note}`}>
                        <Gavel className="w-2.5 h-2.5 shrink-0 text-amber-500" />
                        <span className="text-slate-200 truncate">{h.hearing_time || "—"} {h.dava_no}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Hearings list for this month */}
      <Card className="bg-slate-900/60 border-slate-800">
        <CardContent className="p-5">
          <div className="font-serif text-lg text-slate-100 mb-3">Bu Ay — Tüm Duruşmalar ({hearings.length})</div>
          {hearings.length === 0 ? (
            <div className="text-sm text-slate-500 italic py-6 text-center">Bu ay için duruşma kaydı yok</div>
          ) : (
            <div className="divide-y divide-slate-800">
              {hearings.map((h) => (
                <div key={h.id} className="flex items-center gap-4 py-3" data-testid={`hearing-row-${h.id}`}>
                  <div className="text-center min-w-[60px]">
                    <div className="font-mono text-amber-500 text-lg">{h.hearing_date.slice(8, 10)}</div>
                    <div className="text-[10px] uppercase text-slate-500">{MONTHS[parseInt(h.hearing_date.slice(5, 7), 10) - 1].slice(0, 3)}</div>
                  </div>
                  <div className="font-mono text-slate-300 min-w-[60px]">{h.hearing_time || "—"}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-slate-200 font-medium font-mono">{h.dava_no}</div>
                    <div className="text-xs text-slate-500 truncate">{h.mahkeme_adi}</div>
                    {h.note && <div className="text-xs text-slate-400 italic mt-0.5">"{h.note}"</div>}
                  </div>
                  {statusBadge(h.status)}
                  <button onClick={() => removeHearing(h.id)} className="text-slate-500 hover:text-red-400" data-testid={`hearing-delete-${h.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100" data-testid="new-hearing-dialog">
          <DialogHeader><DialogTitle className="font-serif text-amber-500">Duruşma Ekle</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-400">Dava *</Label>
              <Select value={form.case_id} onValueChange={(v) => setForm({ ...form, case_id: v })}>
                <SelectTrigger className="bg-slate-800 border-slate-700 mt-1" data-testid="hearing-case-select"><SelectValue placeholder="Dava seçin..." /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 max-h-72">
                  {cases.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.dava_no} — {c.mahkeme_adi}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-400">Tarih *</Label>
                <Input type="date" value={form.hearing_date} onChange={(e) => setForm({ ...form, hearing_date: e.target.value })}
                  className="bg-slate-800 border-slate-700 mt-1" data-testid="hearing-date-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-400">Saat</Label>
                <Input type="time" value={form.hearing_time} onChange={(e) => setForm({ ...form, hearing_time: e.target.value })}
                  className="bg-slate-800 border-slate-700 mt-1" data-testid="hearing-time-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-400">Durum</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="bg-slate-800 border-slate-700 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="planlanmis">Planlanmış</SelectItem>
                  <SelectItem value="yapildi">Yapıldı</SelectItem>
                  <SelectItem value="ertelendi">Ertelendi</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-400">Not</Label>
              <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                className="bg-slate-800 border-slate-700 mt-1" data-testid="hearing-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setNewOpen(false)}>İptal</Button>
            <Button onClick={submit} className="bg-amber-600 hover:bg-amber-700 text-slate-950" data-testid="hearing-save-btn">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
